<section class="overview-page customer-detail">
  <app-page-header
    [eyebrow]="customer()?.name ?? ''"
    [title]="('customerDetail.headlinePrefix' | translate) + ' ' + (customer()?.name ?? '')"
  >
    <ng-container right>
      <app-stat-group [items]="stats()" />
      @if (isPlatformAdmin()) {
        <button type="button" class="customer-detail__invite-btn" (click)="onInviteUser()">
          <mat-icon aria-hidden="true">add</mat-icon>
          <span>{{ 'customerDetail.inviteUser' | translate }}</span>
        </button>
      }
    </ng-container>
  </app-page-header>

  @if (loading()) {
    <div class="overview-page__loading">
      <mat-spinner diameter="32"></mat-spinner>
    </div>
  } @else if (error()) {
    <p class="overview-page__hint">{{ error()! | translate }}</p>
  } @else {
    <div class="overview-page__toolbar">
      <app-search-bar
        [placeholder]="'customerDetail.table.searchPlaceholder' | translate"
        [value]="searchTerm()"
        (valueChange)="onSearchChange($event)"
      />
    </div>

    <div class="overview-page__table-card" appScrollContainer>
      <table mat-table [dataSource]="pagedUsers()" class="overview-page__table">
        <ng-container matColumnDef="name">
          <th mat-header-cell *matHeaderCellDef scope="col">{{ 'customerDetail.table.name' | translate }}</th>
          <td mat-cell *matCellDef="let row">
            <div class="customer-detail__name-cell">
              <app-avatar [name]="fullName(row)" [size]="36" />
              <div class="customer-detail__name-text">
                <div class="customer-detail__name-primary">{{ fullName(row) }}</div>
                <div class="customer-detail__name-secondary">{{ row.email }}</div>
              </div>
            </div>
          </td>
        </ng-container>

        <ng-container matColumnDef="email">
          <th mat-header-cell *matHeaderCellDef scope="col">{{ 'customerDetail.table.email' | translate }}</th>
          <td mat-cell *matCellDef="let row">{{ row.email }}</td>
        </ng-container>

        <ng-container matColumnDef="lastLogin">
          <th mat-header-cell *matHeaderCellDef scope="col">
            {{ 'customerDetail.table.lastLogin' | translate }}
          </th>
          <td mat-cell *matCellDef="let row">{{ row.lastLogin | date: 'dd.MM.yyyy HH:mm' }}</td>
        </ng-container>

        <ng-container matColumnDef="role">
          <th mat-header-cell *matHeaderCellDef scope="col">{{ 'customerDetail.table.role' | translate }}</th>
          <td mat-cell *matCellDef="let row">
            <app-role-badge [role]="row.role" />
          </td>
        </ng-container>

        <ng-container matColumnDef="status">
          <th mat-header-cell *matHeaderCellDef scope="col">{{ 'customerDetail.table.status' | translate }}</th>
          <td mat-cell *matCellDef="let row">
            <app-status-pill [kind]="statusKind(row.status, row.expired)" size="sm" />
          </td>
        </ng-container>

        <ng-container matColumnDef="invitationValid">
          <th mat-header-cell *matHeaderCellDef scope="col">
            {{ 'customerDetail.table.invitationValid' | translate }}
          </th>
          <td mat-cell *matCellDef="let row">
            @if (isInvitationExpired(row)) {
              <div class="customer-detail__invitation-valid">
                <span
                  class="customer-detail__invitation-valid-primary customer-detail__invitation-valid-primary--expired"
                >
                  {{ 'customerDetail.table.invitationExpired' | translate }}
                </span>
                <span class="customer-detail__invitation-valid-secondary">
                  {{ 'customerDetail.table.invitationSent' | translate: { date: (row.sentAt | date: 'dd.MM.yyyy') } }}
                </span>
              </div>
            } @else if (row.status === 'PENDING' && row.remainingDays !== undefined && row.remainingDays !== null) {
              <div class="customer-detail__invitation-valid">
                <span class="customer-detail__invitation-valid-primary">
                  {{ 'customerDetail.table.invitationRemaining' | translate: { days: row.remainingDays } }}
                </span>
                <span class="customer-detail__invitation-valid-secondary">
                  {{ 'customerDetail.table.invitationSent' | translate: { date: (row.sentAt | date: 'dd.MM.yyyy') } }}
                </span>
              </div>
            } @else {
              <span>—</span>
            }
          </td>
        </ng-container>

        <ng-container matColumnDef="action">
          <th mat-header-cell *matHeaderCellDef scope="col" class="col-actions">
            {{ 'customerDetail.table.action' | translate }}
          </th>
          <td mat-cell *matCellDef="let row">
            <div class="overview-page__action-cell">
              @if (canResendInvitation(row)) {
                <button
                  type="button"
                  class="customer-detail__resend-btn"
                  [class.customer-detail__resend-btn--sent]="isInvitationResent(row.id)"
                  [disabled]="isInvitationResent(row.id) || isInvitationResending(row.id)"
                  aria-live="polite"
                  aria-atomic="true"
                  (click)="onResendInvitation(row)"
                >
                  <mat-icon aria-hidden="true">{{ isInvitationResent(row.id) ? 'check' : 'autorenew' }}</mat-icon>
                  <span>
                    {{
                      (isInvitationResent(row.id)
                        ? 'customerDetail.actions.resendSent'
                        : 'customerDetail.actions.resend'
                      ) | translate
                    }}
                  </span>
                </button>
              }
              <app-user-row-actions-menu
                [user]="row"
                [canDelete]="isPlatformAdmin() && !isOwnUser(row.id)"
                [removeLabelKey]="removeActionLabelKey(row)"
                (viewProfile)="onViewProfile($event)"
                (manageRoles)="onChangeRole($event)"
                (removeUser)="onRemoveUser($event)"
              />
            </div>
          </td>
        </ng-container>

        <tr mat-header-row *matHeaderRowDef="displayedColumns()"></tr>
        <tr mat-row *matRowDef="let row; columns: displayedColumns()" class="overview-page__row"></tr>

        <tr class="mat-row" *matNoDataRow>
          <td class="mat-cell" [attr.colspan]="displayedColumns().length">
            <span>{{ noDataMessageKey() | translate }}</span>
          </td>
        </tr>
      </table>
    </div>

    <app-overview-pagination
      [pageIndex]="pageIndex()"
      [pageSize]="pageSize()"
      [totalCount]="filteredUsers().length"
      (pageIndexChange)="pageIndex.set($event)"
      (pageSizeChange)="onPageSizeChange($event)"
    />
  }
</section>

<app-change-role-dialog
  [visible]="showRoleDialog()"
  [user]="selectedUser()"
  [roles]="roles()"
  (save)="onRoleSave($event)"
  (dialogCancel)="onRoleDialogCancel()"
  (visibleChange)="showRoleDialog.set($event)"
></app-change-role-dialog>

<ng-template #deleteUserConfirmTemplate>
  <div class="customer-detail__delete-dialog">
    <p class="customer-detail__delete-message">
      {{ deleteMessageKey() | translate: { name: userToDelete()?.displayName } }}
    </p>
    @if (blockingProjects().length > 0) {
      <div class="customer-detail__blocking" role="alert">
        <p class="customer-detail__blocking-label">
          {{ 'customerDetail.delete.blockingProjectsLabel' | translate }}
        </p>
        <ul class="customer-detail__blocking-list">
          @for (project of blockingProjects(); track project.id) {
            <li>{{ project.name }}</li>
          }
        </ul>
      </div>
    }
  </div>
</ng-template>

<lib-modal
  [visible]="showDeleteDialog()"
  [title]="deleteTitleKey() | translate"
  [contentTemplate]="deleteUserConfirmTemplate"
  [saveText]="deleteConfirmButtonKey() | translate"
  [cancelText]="'customerDetail.delete.cancelButton' | translate"
  [disabled]="deleting() || blockingProjects().length > 0"
  (save)="onDeleteConfirmed()"
  (cancel)="onDeleteCancelled()"
  (visibleChange)="!$event && onDeleteCancelled()"
></lib-modal>
